import React, { useContext, useState } from 'react';
import { useMemo } from 'react';

import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput, TextInputProps } from '@components/NextForms/TextInput';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import { MobxZodTypes, MobxZodField } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { mapOptional } from '@utils/types';
import clsx from 'clsx';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    before: '変更前',
    after: '変更後',
    detail: '変更理由',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  bookingFormContainer: {
    ...common.middleText,
    ...common.textGray700,
    display: 'grid',
    gridTemplateColumns: 'auto minmax(150px, auto) 1fr minmax(250px, auto)',
    rowGap: 10,
  },
  bookingFormItemWrapper: {
    ...common.contentsHoverPrimary,
  },
  bookingFormCell: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  bookingFormCellDisplay: {
    paddingTop: `calc(8px + 6px)`,
  },
  bookingFormAction: {
    ...common.flexInline,
    ...common.justifyEnd,
    paddingRight: 72,
  },
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1rem 2rem',
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 550,
  },
}));

export interface BookingFormContextValuesInput {
  mode: 'edit' | 'display';
  isLocked?: boolean;
  detailField: MobxZodField<z.ZodString>;
  onEditClick: () => void;
  onCancelEdit: () => void;
}

const BookingFormContext = React.createContext<Required<BookingFormContextValuesInput>>(
  undefined as any,
);

export const BookingFormContainer: React.FC<
  React.PropsWithChildren<BookingFormContextValuesInput>
> = ({ mode, isLocked = false, detailField, onEditClick, onCancelEdit, children }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <BookingFormContext.Provider value={{ mode, isLocked, detailField, onEditClick, onCancelEdit }}>
      <div className={classes.bookingFormContainer}>{children}</div>
    </BookingFormContext.Provider>
  );
};

export const BookingFormInputIndex: React.FC<{ index?: number }> = React.memo(({ index }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div
      className={clsx(classes.bookingFormCell, classes.bookingFormCellDisplay)}
      style={{ paddingLeft: 40, paddingRight: 25 }}
    >
      {index}
    </div>
  );
});

export const BookingFormInputLabel: React.FC<{ label?: string }> = React.memo(({ label }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.bookingFormCell, classes.bookingFormCellDisplay)}>{label}</div>
  );
});

export type BookingFormInputProps<T extends MobxZodTypes> = {
  /**
   * The index of the input
   */
  index?: number;
  /**
   * Whether to show the open edit button on the right
   */
  canEdit?: boolean;
  /**
   * Override the default dialog content
   */
  editDialogElement?: React.ReactNode;
} & TextInputProps<T>;

export const BookingFormInput = <T extends MobxZodTypes>(props: BookingFormInputProps<T>) => {
  const { field, index, canEdit = true, editDialogElement } = props;

  const { bookingInquiry, preAlert, user } = useContext(BookingDetailsContext);
  const canEditBookingInquiry = new AdminInquiryDetailHelper(bookingInquiry).canEdit(
    user,
    preAlert,
  );

  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const initialRawInput = useMemo(
    () =>
      mapOptional(field.type.getFormMeta().rawValueDecoder, (r) => r(field.rawInput)) ??
      field.rawInput,
    [],
  );

  const { mode, isLocked, detailField, onEditClick, onCancelEdit } = useContext(BookingFormContext);

  const meta = useMemo(() => {
    return field.type.getFormMeta();
  }, [field.type]);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const onClose = () => {
    setEditDialogOpen(false);
    onCancelEdit();
  };

  const renderEditDialog = () => {
    return (
      <ActionableDialog open={editDialogOpen} onClose={onClose} size="md">
        {!editDialogElement && (
          <>
            <ActionableDialogTitle>{field.type.getFormMeta().label}</ActionableDialogTitle>

            <div className={classes.dialogContent}>
              <IndexedFormContainer size="md">
                <IndexedLabel label={strings.before} />
                <div role="data" className={classes.whitespacePreWrap}>
                  {initialRawInput as any}
                </div>

                <IndexedLabel label={strings.after} field={field} />
                <div>
                  <TextInput {...props} />
                </div>

                <IndexedLabel field={detailField} />
                <div>
                  <TextInput field={detailField} multiline />
                </div>
              </IndexedFormContainer>
            </div>
          </>
        )}

        {editDialogElement}

        <ActionableDialogActions justifyContent="right">
          <Button
            color="outlined"
            size="small"
            text={formStrings.cancel}
            type="button"
            onClick={onClose}
          />
          <Button
            type="submit"
            color="primary"
            size="small"
            text={formStrings.update}
            onClick={() => {
              setEditDialogOpen(false);
              onEditClick();
            }}
          />
        </ActionableDialogActions>
      </ActionableDialog>
    );
  };

  const { confirm, renderDialog } = useConfirmDialog();

  return (
    <div className={classes.bookingFormItemWrapper}>
      {renderEditDialog()}
      {renderDialog()}

      <BookingFormInputIndex index={index} />
      <BookingFormInputLabel label={meta.label} />
      <div className={classes.bookingFormCell}>
        <TextInput
          {...props}
          {...(mode === 'display' ? { inputOptions: { type: 'display' } } : undefined)}
          inputProps={{
            style: {
              opacity: editDialogOpen ? 0 : 1, // Hide the display because it changes with the dialog input
              transition: 'opacity',
            },
          }}
        />
      </div>
      <div
        className={clsx(
          classes.bookingFormCell,
          classes.bookingFormCellDisplay,
          classes.bookingFormAction,
        )}
      >
        {mode === 'display' && canEdit && (
          <a
            className={clsx(
              classes.link,
              isLocked && classes.linkDisabled,
              canEditBookingInquiry?._tag === 'Left' && classes.textGray300Important,
            )}
            onClick={
              isLocked
                ? undefined
                : () => {
                    if (canEditBookingInquiry?._tag === 'Right') {
                      setEditDialogOpen(true);
                    } else {
                      confirm({
                        message: canEditBookingInquiry?.left.messages.join('\n'),
                      });
                    }
                  }
            }
          >
            {formStrings.edit}
          </a>
        )}
      </div>
    </div>
  );
};
